import React, {Component} from "react";
import {createBrowserHistory} from "history";
import {Redirect, Route as NeutralRoute, Router, Switch} from "react-router-dom";
import Cookie from "./util/cookie";
import {_ENV_MODE} from "./util/env";
import Login from "./views/auth/login";
import NotFoundView from "./views/general/not-found";
import ResetPassword from "./views/auth/reset-password";
import ResetPasswordConfirm from "./views/auth/reset-password-confirm";
import ContactsView from "./views/contacts/contacts-view";
import GroupsView from "./views/my-company/groups-view";
import OfficesView from "./views/my-company/offices-view";
import DivisionsView from "./views/my-company/divisions-view";
import SystemUsersView from "./views/my-company/system-users-view";
import ContactPermissionsView from "./views/my-company/contact-permissions-view";
import VendorsView from "./views/companies/vendors";
import VendorCreateView from "./views/companies/vendor-create";
import VendorView from "./views/companies/vendor";
import AllTasksView from "./views/tasks/all-tasks";
import TasksSettingsView from "./views/tasks/tasks-settings-view";
import TaskGroupView from "./views/tasks/group-tasks";
import RepeatTasksView from "./views/tasks/repeatTasks";
import Resources from "./data/services/resources";
import ExpiryItemsView from "./views/general/expiry-items-view";
import Profiles from "./views/general/profiles";
import AccountView from "./views/general/account-view";
import LoggedDevicesView from "./views/general/logged-devices-view";
import TruckCreateView from "./views/fleet/truck-create";
import TrucksView from "./views/fleet/trucks-view";
import TruckView from "./views/fleet/truck";
import TrailersView from "./views/fleet/trailers";
import TrailerCreateView from "./views/fleet/trailer-create";
import TrailerView from "./views/fleet/trailer";
import FleetSettingsView from "./views/fleet/fleet-settings-view";
import WorkOrdersView from "./views/work-orders/work-orders-view";
import WorkOrdersCreate from "./views/work-orders/work-orders-create";
import WorkOrdersSingleView from "./views/work-orders/work-orders-single-view";
import PartsView from "./views/parts-view";
import RepairCodesView from "./views/settings/repair-codes-view";
import LoggingInView from "./views/general/logging-in";
import LocalStorage from "./common/util/localStorage";
import {getUser, isTokenExpired} from "./util/util-auth";
import TaskGrabData from "./common/components/layout/layout-components/task-grab-data";
import {generateCustomNotification} from "./common/util/util-helpers";
import Notifications from "./common/components/notifications";
import AgentsView from "./views/personnel/agents-view";
import AgentSingleView from "./views/personnel/agent-single";
import EmployeeSingleView from "./views/personnel/employee-single";
import EmployeesPageView from "./views/personnel/employees-page";
import AccountingSummaryPageView from "./views/accounting/summary";
import IncomePageView from "./views/accounting/income/income-page";
import ExpensePageView from "./views/accounting/expense/expense-page";
import AccountingReportsPageView from "./views/accounting/reports/reports-page";
import PurchaseOrder from "./views/accounting/purchase-order-page";
import PurchaseOrderSingleView from "./views/accounting/purchase-order-single";
import ProductsServicesPageView from "./views/accounting/products-services";
import AssetsInventoryPageView from "./views/accounting/assets-inventory";
import TaxesPage from "./views/accounting/taxes/taxes-page";
import JournalView from "./views/accounting/journal";
import AccountingPageView from "./views/accounting/chart-of-accounts/chart-of-accounts";
import PayrollView from "./views/accounting/driver-payroll";
import AccountingAssetCreateView from "./views/accounting/assets-inventory/create-accounting-asset-view";
import AccountingSettingsView from "./views/accounting/accounting-settings";
import AccountingReimbursementPageView from "./views/accounting/accounting-reimbursement-view";
import AccountingReconciliationsView from "./views/accounting/reconciliation/reconciliations-view";
import CreditAccountsPageView from "./views/accounting/credit-accounts";
import TransactionDetailsView from "./views/accounting/credit-accounts/single-account-transaction-details";
import MoneyCodesPage from "./views/accounting/money-codes-view";
import AccountingReconciliationView from "./views/accounting/reconciliation";
import UiToolsView from "./views/general/settings/ui-tools-view";
import IntegrationsTab from "./views/general/settings/integrations";
import CompaniesSettingsView from "./views/companies/companies-settings-view";
import TicketsView from "./views/general/tickets-view";
import ServiceBaysView from "./views/service-bays-view";
import CalendarTab from "./views/work-orders-calendar";
import WorkOrderSettingsView from "./views/settings";
import CustomerBillingWorkOrder from "./views/accounting/customer-billing-work-order";
import CustomersView from "./views/companies/customers-view";
import CustomerCreateNew from "./views/companies/customer-create";
import CustomerView from "./views/companies/customer";
import WorkOrderTemplatesView from "./views/work-orders/work-orders-templates";
import WoTemplateSingle from "./views/work-orders/work-orders-templates-single/wo-template-single";
import WoTemplateCreate from "./views/work-orders/work-orders-templates-create/wo-template-create";
import DocumentsView from "./views/documents";
import SettingsView from "./views/general/settings";
import UsersReportView from "./views/general/users-report-view";
import ScheduledMaintenancePageView from "./views/fleet/maintenance-view";
import WorkOrdersReports from "./views/work-orders/work-orders-reports";
import DownloadCenter from "./views/download-center";


const PrivateRoute = ({component: Component, ...rest}) => (
    <NeutralRoute
        {...rest}
        render={props => {
            const token = Cookie.getCookie("access_token_" + _ENV_MODE);

            if (token) {
                if (
                    !LocalStorage.has("user")
                    || getUser("access_token") !== token
                    || isTokenExpired(token)
                ) {
                    LocalStorage.set("redirect_path", props.location.search ? props.match.url + props.location.search : props.match.url);

                    return <LoggingInView {...props} />;
                }

                return <Component {...props} />;
            }

            LocalStorage.set("redirect_path", props.location.search ? props.match.url + props.location.search : props.match.url);

            return (
                <Redirect to={{
                    pathname: "/login",
                    state: {from: props.location}
                }}/>
            )
        }}
    />
);

const PublicRoute = ({component: Component, ...rest}) => {
    const token = Cookie.getCookie("access_token_" + _ENV_MODE);

    return (
        <NeutralRoute
            {...rest}
            render={props =>
                (!token) ? (<Component {...props}/>) : (
                    <Redirect to={{
                        pathname: "/home",
                        state: {from: props.location}
                    }}
                    />
                )
            }
        />
    )
};

export default function Routes(store, translator) {
    function getComponent(Component, props) {
        return (
            <Component {...props} translate={(key, args) => translator.translate(key, args)}/>
        );
    }

    const history = createBrowserHistory();
    store.history = history;

    return (
        <Router history={history}>
            <Switch>
                <PrivateRoute exact path="/" component={(props) => getComponent(WorkOrdersView, props)}/>
                <PublicRoute exact path="/logging-in" component={(props) => getComponent(LoggingInView, props)}/>
                <PrivateRoute exact path="/home" component={(props) => getComponent(WorkOrdersView, props)}/>

                {/* Auth routes */}
                <PublicRoute exact path="/login" component={(props) => getComponent(Login, props)}/>
                <PublicRoute exact path="/reset-password"
                             component={(props) => getComponent(ResetPassword, props)}/>
                <NeutralRoute exact path="/reset-password-confirm/:email/:token/:is_new"
                              component={(props) => getComponent(ResetPasswordConfirm, props)}/>
                <PrivateRoute exact path="/users-report"
                              component={(props) => getComponent(UsersReportView, props)}/>

                {/* Contact routes */}
                <PrivateRoute exact path="/contacts" component={(props) => getComponent(ContactsView, props)}/>

                {/* My Company routes */}
                <PrivateRoute exact path="/offices" component={(props) => getComponent(OfficesView, props)}/>
                <PrivateRoute exact path="/groups" component={(props) => getComponent(GroupsView, props)}/>
                <PrivateRoute exact path="/divisions" component={(props) => getComponent(DivisionsView, props)}/>
                <PrivateRoute exact path="/system-users"
                              component={(props) => getComponent(SystemUsersView, props)}/>
                <PrivateRoute exact path="/contacts/:id"
                              component={(props) => getComponent(ContactPermissionsView, props)}/>

                {/*Task routes*/}
                <PrivateRoute exact path="/all-tasks" component={(props) => getComponent(AllTasksView, props)}/>
                <PrivateRoute exact path="/board-tasks" component={(props) => getComponent(TaskGroupView, props)}/>
                <PrivateRoute exact path="/repeat-tasks"
                              component={(props) => getComponent(RepeatTasksView, props)}/>
                <PrivateRoute exact path="/tasks-settings"
                              component={(props) => getComponent(TasksSettingsView, props)}/>


                {/* Companies routes */}
                <PrivateRoute exact path="/customers" component={(props) => getComponent(CustomersView, props)}/>
                <PrivateRoute exact path="/customer/create"
                              component={(props) => getComponent(CustomerCreateNew, props)}/>
                <PrivateRoute exact path="/customers/info/:OrganizationID/:CustomerID"
                              component={(props) => getComponent(CustomerView, props)}/>
                {/*<PrivateRoute exact path="/carriers/info/:OrganizationID/:id"*/}
                {/*              component={(props) => getComponent(CarrierView, props)}/>*/}

                <PrivateRoute exact path="/vendors" component={(props) => getComponent(VendorsView, props)}/>
                <PrivateRoute exact path="/vendor/create"
                              component={(props) => getComponent(VendorCreateView, props)}/>
                <PrivateRoute exact path="/vendors/info/:OrganizationID/:VendorID"
                              component={(props) => getComponent(VendorView, props)}/>
                <PrivateRoute exact path="/companies-expiry-items"
                              component={(props) => getComponent(ExpiryItemsView, Object.assign({}, props, {resourceName: Resources.CompaniesExpiry}))}/>
                <PrivateRoute exact path="/companies-settings"
                              component={(props) => getComponent(CompaniesSettingsView, props)}/>
                <PrivateRoute exact path="/download-center"
                              component={(props) => getComponent(DownloadCenter, props)}/>

                {/*Fleet*/}
                <PrivateRoute exact path="/trucks" component={(props) => getComponent(TrucksView, props)}/>
                <PrivateRoute exact path="/truck/create"
                              component={(props) => getComponent(TruckCreateView, props)}/>
                <PrivateRoute exact path="/trucks/info/:id" component={(props) => getComponent(TruckView, props)}/>
                <PrivateRoute exact path="/trailers" component={(props) => getComponent(TrailersView, props)}/>
                <PrivateRoute exact path="/trailer/create"
                              component={(props) => getComponent(TrailerCreateView, props)}/>
                <PrivateRoute exact path="/trailers/info/:id"
                              component={(props) => getComponent(TrailerView, props)}/>
                <PrivateRoute exact path="/expiry-items"
                              component={(props) => getComponent(ExpiryItemsView, Object.assign({}, props, {resourceName: Resources.FleetExpiry}))}/>
                <PrivateRoute exact path="/fleet-settings"
                              component={(props) => getComponent(FleetSettingsView, props)}/>
                <PrivateRoute exact path="/maintenance" component={(props) => getComponent(ScheduledMaintenancePageView, props)}/>

                {/*Work Orders*/}
                <PrivateRoute exact path="/work-orders" component={(props) => getComponent(WorkOrdersView, props)}/>
                <PrivateRoute exact path="/work-orders-create"
                              component={(props) => getComponent(WorkOrdersCreate, props)}/>
                <PrivateRoute exact path="/work-orders/:id"
                              component={(props) => getComponent(WorkOrdersSingleView, props)}/>
                <PrivateRoute exact path="/work-orders/:id/parts"
                              component={(props) => getComponent(WorkOrdersSingleView, props)}/>
                <PrivateRoute exact path="/work-orders-reports"
                              component={(props) => getComponent(WorkOrdersReports, props)}/>

                <PrivateRoute exact path="/parts" component={(props) => getComponent(PartsView, props)}/>
                <PrivateRoute exact path="/service-bays" component={(props) => getComponent(ServiceBaysView, props)}/>
                <PrivateRoute exact path="/calendar" component={(props) => getComponent(CalendarTab, props)}/>
                <PrivateRoute exact path="/repair-codes"
                              component={(props) => getComponent(RepairCodesView, props)}/>

                <PrivateRoute exact path="/work-order/settings"
                              component={(props) => getComponent(WorkOrderSettingsView, props)}/>
                <PrivateRoute exact path="/work-orders-templates"
                              component={(props) => getComponent(WorkOrderTemplatesView, props)}/>

                <PrivateRoute exact path="/work-orders-templates/:id"
                              component={(props) => getComponent(WoTemplateSingle, props)}/>

                <PrivateRoute exact path="/work-orders-templates-create"
                              component={(props) => getComponent(WoTemplateCreate, props)}/>

                {/* Personnel routes */}
                <PrivateRoute exact path="/employees-page"
                              component={(props) => getComponent(EmployeesPageView, props)}/>
                <PrivateRoute exact path="/employees/create"
                              component={(props) => getComponent(EmployeeSingleView, props)}/>
                <PrivateRoute exact path="/employees/info/:id"
                              component={(props) => getComponent(EmployeeSingleView, props)}/>

                <PrivateRoute exact path="/agents" component={(props) => getComponent(AgentsView, props)}/>
                <PrivateRoute exact path="/agent-create"
                              component={(props) => getComponent(AgentSingleView, props)}/>
                <PrivateRoute exact path="/agents/info/:id"
                              component={(props) => getComponent(AgentSingleView, props)}/>

                {/*<PrivateRoute exact path="/drivers" component={(props) => getComponent(DriversView, props)}/>*/}
                {/*<PrivateRoute exact path="/driver/create"*/}
                {/*              component={(props) => getComponent(DriverCreateView, props)}/>*/}
                {/*<PrivateRoute exact path="/drivers/info/:id"*/}
                {/*              component={(props) => getComponent(DriverCreateView, props)}/>*/}
                {/*<PrivateRoute exact path="/drivers/settlements/template/:id"*/}
                {/*              component={(props) => getComponent(DriverSettlementsTemplateCreateEdit, props)}/>*/}
                {/*<PrivateRoute exact path="/drivers/settlements/template"*/}
                {/*              component={(props) => getComponent(DriverSettlementsTemplateCreateEdit, props)}/>*/}


                {/*/!* Accounting *!/*/}
                <PrivateRoute exact path="/accounting-summary"
                              component={(props) => getComponent(AccountingSummaryPageView, props)}/>
                <PrivateRoute exact path="/accounting-income"
                              component={(props) => getComponent(IncomePageView, props)}/>
                <PrivateRoute exact path="/accounting-expenses"
                              component={(props) => getComponent(ExpensePageView, props)}/>
                <PrivateRoute exact path="/accounting-reports"
                              component={(props) => getComponent(AccountingReportsPageView, props)}/>
                {/*<PrivateRoute exact path="/accounting-billing"*/}
                {/*              component={(props) => getComponent(BillingView, props)}/>*/}
                <PrivateRoute exact path="/accounting-purchase-order"
                              component={(props) => getComponent(PurchaseOrder, props)}/>
                <PrivateRoute exact path="/purchase-order/create"
                              component={(props) => getComponent(PurchaseOrderSingleView, props)}/>
                <PrivateRoute exact path="/purchase-order/:id"
                              component={(props) => getComponent(PurchaseOrderSingleView, props)}/>

                <PrivateRoute exact path="/accounting-products-services"
                              component={(props) => getComponent(ProductsServicesPageView, props)}/>
                <PrivateRoute exact path="/accounting-assets-inventory"
                              component={(props) => getComponent(AssetsInventoryPageView, props)}/>
                <PrivateRoute exact path="/accounting-taxes"
                              component={(props) => getComponent(TaxesPage, props)}/>
                <PrivateRoute exact path="/accounting-journal"
                              component={(props) => getComponent(JournalView, props)}/>
                <PrivateRoute exact path="/accounting-accounts"
                              component={(props) => getComponent(AccountingPageView, props)}/>
                <PrivateRoute exact path="/accounting-assets/create"
                              component={(props) => getComponent(AccountingAssetCreateView, props)}/>
                <PrivateRoute exact path="/accounting-assets/:id"
                              component={(props) => getComponent(AccountingAssetCreateView, props)}/>
                <PrivateRoute exact path="/driver-payroll"
                              component={(props) => getComponent(PayrollView, props)}/>
                {/*<PrivateRoute exact path="/carrier-pay"*/}
                {/*              component={(props) => getComponent(CarrierPayrollView, props)}/>*/}
                <PrivateRoute exact path="/accounting-settings"
                              component={(props) => getComponent(AccountingSettingsView, props)}/>
                <PrivateRoute exact path="/accounting-reimbursement"
                              component={(props) => getComponent(AccountingReimbursementPageView, props)}/>
                <PrivateRoute exact path="/accounting/reconciliations"
                              component={(props) => getComponent(AccountingReconciliationsView, props)}/>
                <PrivateRoute exact path="/accounting/reconciliation/:id"
                              component={(props) => getComponent(AccountingReconciliationView, props)}/>
                <PrivateRoute exact path="/accounting-credit-cards"
                              component={(props) => getComponent(CreditAccountsPageView, props)}/>
                <PrivateRoute exact path="/customer-billing"
                              component={(props) => getComponent(CustomerBillingWorkOrder, props)}/>
                <PrivateRoute exact path="/accounting/credit-cards/:id"
                              component={(props) => getComponent(TransactionDetailsView, props)}/>
                <PrivateRoute exact path="/money-codes"
                              component={(props) => getComponent(MoneyCodesPage, props)}/>

                {/*General routes*/}
                <PrivateRoute exact path="/tools" component={(props) => getComponent(UiToolsView, props)}/>
                <PrivateRoute exact path="/integrations-center"
                              component={(props) => getComponent(IntegrationsTab, props)}/>
                <PrivateRoute exact path="/my-profile" component={(props) => getComponent(Profiles, props)}/>
                <PrivateRoute exact path="/ticketing" component={(props) => getComponent(TicketsView, props)}/>
                <PrivateRoute exact path="/documents" component={(props) => getComponent(DocumentsView, props)}/>
                <PrivateRoute exact path="/account" component={(props) => getComponent(AccountView, props)}/>
                <PrivateRoute exact path="/settings" component={(props) => getComponent(SettingsView, props)}/>
                <PrivateRoute exact path="/logged-devices"
                              component={(props) => getComponent(LoggedDevicesView, props)}/>
                <NeutralRoute component={(props) => getComponent(NotFoundView, props)}/>
            </Switch>

            <Notifications
                history={history} dispatch={store.dispatch} {...store.getState()}
                translate={(key, args) => translator.translate(key, args)}
                generateNotificationBaseOnTitle={generateCustomNotification}
            />

            <TaskGrabData
                dispatch={store.dispatch}
            />
        </Router>
    );
}
